<template>
  <div class="space-y-4">
    <!-- Dark Theme Preference -->
    <div class="flex items-center justify-between p-4 bg-slate-50 dark:bg-gray-700/50 rounded-xl border border-slate-100 dark:border-gray-700">
      <div class="flex items-center space-x-3">
        <div class="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
          <MoonIcon class="w-5 h-5" />
        </div>
        <div>
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ $t('profileSection.darkTheme', 'Tema Escuro') }}
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ $t('profileSection.darkThemeDesc', 'Alterne entre o tema claro e escuro para maior conforto visual.') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        @click="toggleTheme"
        class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
        :class="isDarkTheme ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'"
      >
        <span
          class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out"
          :class="isDarkTheme ? 'translate-x-5' : 'translate-x-0'"
        />
      </button>
    </div>

    <!-- Realtime Updates Preference -->
    <div class="flex items-center justify-between p-4 bg-slate-50 dark:bg-gray-700/50 rounded-xl border border-slate-100 dark:border-gray-700">
      <div class="flex items-center space-x-3">
        <div class="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
          <ArrowPathIcon class="w-5 h-5" />
        </div>
        <div>
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ $t('profileSection.realtimeUpdates', 'Atualizações em Tempo Real') }}
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ $t('profileSection.realtimeUpdatesDesc', 'Sincronize alterações de cifras, membros e playlists instantaneamente.') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        @click="toggleRealtime"
        class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
        :class="enabledRealtime ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'"
      >
        <span
          class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out"
          :class="enabledRealtime ? 'translate-x-5' : 'translate-x-0'"
        />
      </button>
    </div>

    <!-- Onboarding Tour Preference -->
    <div class="flex items-center justify-between p-4 bg-slate-50 dark:bg-gray-700/50 rounded-xl border border-slate-100 dark:border-gray-700">
      <div class="flex items-center space-x-3">
        <div class="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
          <QuestionMarkCircleIcon class="w-5 h-5" />
        </div>
        <div>
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ $t('profileSection.onboardingTour', 'Tour de Boas-Vindas') }}
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ $t('profileSection.onboardingTourDesc', 'Reinicie o tutorial guiado para rever os recursos da plataforma.') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        @click="handleReplayTour"
        class="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors shadow-xs cursor-pointer active:scale-95"
      >
        {{ $t('tour.restartTour', 'Reiniciar Tour') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { MoonIcon, ArrowPathIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline';
import appStore from '@/store/app.store';
import { appEvents } from '@/store/app.events';
import { updateStatusBarTheme } from '@/utils/capacitor';
import { useTour } from '@/composables/useTour';

const router = useRouter();
const { startDashboardTour, resetDashboardTour } = useTour();

const handleReplayTour = () => {
  resetDashboardTour();
  router.push('/').then(() => {
    setTimeout(() => {
      startDashboardTour(true);
    }, 250);
  });
};

const enabledRealtime = computed(() => appStore.state.enabledRealtime);

const isDarkTheme = ref(
  localStorage.getItem('color-theme') === 'dark' ||
  (!('color-theme' in localStorage) && localStorage.getItem('theme') === 'dark')
);

const toggleTheme = () => {
  isDarkTheme.value = !isDarkTheme.value;
  const newTheme = isDarkTheme.value ? 'dark' : 'light';
  localStorage.setItem('color-theme', newTheme);
  localStorage.setItem('theme', newTheme);

  if (newTheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  updateStatusBarTheme(false);
};

const toggleRealtime = () => {
  const newVal = !enabledRealtime.value;
  appStore.commit('SET_ENABLED_REALTIME', newVal);
  appEvents.emit('enabledRealtimeChanged', newVal);
};
</script>
